import React from 'react';

import { useClient } from '../Client/Client';
import { UserContextType, UserDataContextType } from './types';
import { EMPTY_SCOPE, getScopeMode, ManagedGroup, Scope } from './scope';

type UserProviderProps = {
  children: React.ReactNode;
};

export const UserContext = React.createContext<UserContextType>({
  user: undefined,
  refreshUser: () => {},
  scope: EMPTY_SCOPE,
});

export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
  const [me, setMe] = React.useState<UserDataContextType>(undefined);
  const [managedGroups, setManagedGroups] = React.useState<
    ManagedGroup[] | undefined
  >(undefined);
  const { status, get } = useClient();
  React.useEffect(() => {
    if (status === 'authenticated') {
      return get<{ data: UserDataContextType }>('/me', ({ data }) =>
        setMe(data)
      );
    } else {
      setMe(undefined);
      setManagedGroups(undefined);
    }
  }, [status, get]);

  // Les groupes administrés (GET /me/groups/managed) déterminent le périmètre :
  // un responsable de communauté n'a pas de Réseau et Pilotage se limite à
  // ses communautés. Rechargé avec /me (promotion, révocation).
  React.useEffect(() => {
    if (!me?.id) return;
    return get<{ data: ManagedGroup[] }>(
      '/me/groups/managed',
      ({ data }) => setManagedGroups(data ?? []),
      () => setManagedGroups([])
    );
  }, [me?.id, me?.customer_role?.name, get]);

  const refreshUser = () =>
    get<{ data: UserDataContextType }>('/me', ({ data }) => setMe(data));

  const scope = React.useMemo<Scope>(
    () => ({
      mode: getScopeMode(me?.customer_role?.name, managedGroups ?? []),
      managedGroups: managedGroups ?? [],
      loaded: managedGroups !== undefined,
    }),
    [me?.customer_role?.name, managedGroups]
  );

  return (
    <UserContext.Provider value={{ user: me, refreshUser, scope }}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => React.useContext(UserContext);
